// 261023 E3-P3h4c (FE) — «hộp hậu quả» CHỈ ĐỌC trước 4 thao tác: bỏ duyệt khung xương · xoá từ vựng · viết lại tập · hoàn tác bản máy sửa. THUẦN: KHÔNG import antd / react.
// Chữ của từng dòng lấy NGUYÊN từ BE (`ConsequenceEffect.message`, đã đối chiếu với mutation thật; qua `redactMoney`); FE chỉ chọn nhóm / tone / thứ tự.
// Mockup nói rộng hơn hành vi thật ở vài chỗ (xem báo cáo 3h4c, mục LỆCH) ⇒ theo BE.
import type { ConsequenceEffect, ConsequencePreview } from '../../../types/adaptationWorkbench';
import { friendlyApiError } from './jobErrorText';
import { redactMoney } from './moneyRedact';

export type ConsGroupKey = 'becomeOld' | 'willBlock' | 'mayNeedReview' | 'unchanged';
export type ConsTone = 'stale' | 'block' | 'maybe' | 'keep';
export interface ConsGroup { key: ConsGroupKey; title: string; tone: ConsTone; items: string[] }
export interface ConsequenceView {
  canCommit: boolean;
  blockers: string[];
  groups: ConsGroup[];
  /** «Thao tác này có dùng AI» — không nói tiền. */
  aiLine: string | null;
  base: ConsequencePreview['base'];
}

/** Tiêu đề nhóm: hai nhóm đầu và «Không đổi» theo mockup; «Có thể cần xem lại» + «Tạm khoá» do BE bổ sung (mockup chưa có). */
export const CONS_TITLES: Record<ConsGroupKey, string> = {
  becomeOld: 'Sẽ thành Cũ / cần làm lại',
  willBlock: 'Tạm khoá tới khi làm lại bước',
  mayNeedReview: 'Có thể cần xem lại',
  unchanged: 'Không đổi',
};
const TONE: Record<ConsGroupKey, ConsTone> = { becomeOld: 'stale', willBlock: 'block', mayNeedReview: 'maybe', unchanged: 'keep' };
const ORDER: ConsGroupKey[] = ['becomeOld', 'willBlock', 'mayNeedReview', 'unchanged'];
export const CONS_AI_LINE = 'Thao tác này có dùng AI.';
const BLOCKED_GENERIC = 'Chưa thực hiện được thao tác này lúc này — tải lại rồi thử lại';
export const CONS_STALE_TEXT = 'Dữ liệu vừa đổi — đã tải lại hệ quả, xem lại rồi bấm xác nhận.';

const textsOf = (list: unknown): string[] =>
  (Array.isArray(list) ? list : []).map((e) => redactMoney((e as Partial<ConsequenceEffect> | null)?.message).trim()).filter(Boolean);

/**
 * Hình dạng lạ / BE cũ (không phải `version:1` có `action` và các mảng hợp lệ) ⇒ `null` ⇒ caller dùng HỘP XÁC NHẬN CŨ (không chặn người dùng).
 * Không có dòng nào để vẽ (mà vẫn `canCommit`) cũng ⇒ `null`. `canCommit:false` ⇒ vẽ `blockers`, KHÔNG tự thêm «không có tác động».
 */
export const consequenceViewOf = (raw: ConsequencePreview | null | undefined): ConsequenceView | null => {
  const p = raw as Partial<ConsequencePreview> | null | undefined;
  if (!p || typeof p !== 'object' || p.version !== 1 || typeof p.action !== 'string') return null;
  for (const k of ORDER) if (p[k] !== undefined && !Array.isArray(p[k])) return null;
  const canCommit = p.canCommit !== false;
  const groups: ConsGroup[] = ORDER.map((key) => ({ key, title: CONS_TITLES[key], tone: TONE[key], items: textsOf(p[key]) })).filter((g) => g.items.length > 0);
  let blockers: string[] = [];
  if (!canCommit) {
    blockers = (Array.isArray(p.blockers) ? p.blockers : []).map((b) => redactMoney(b?.message).trim()).filter(Boolean);
    if (blockers.length === 0) blockers = [BLOCKED_GENERIC];
  }
  if (canCommit && groups.length === 0) return null;   // BE luôn kèm ≥ 1 dòng «không đổi» khi preview rỗng; không có gì để vẽ ⇒ hộp cũ (không bịa «0 tác động»)
  return { canCommit, blockers, groups, aiLine: p.mayCallAi === true ? CONS_AI_LINE : null, base: p.base ?? {} };
};

/** 409 do dữ liệu đổi giữa lúc xem hệ quả và lúc bấm: `OUTLINE_CHANGED` (bỏ duyệt / xoá từ) · `IMPACT_CHANGED` (viết lại) · `SCRIPT_CHANGED` (hoàn tác). */
const STALE_CODES: ReadonlySet<string> = new Set(['OUTLINE_CHANGED', 'IMPACT_CHANGED', 'SCRIPT_CHANGED']);
export const isStaleConsequenceError = (e: unknown): boolean => {
  const code = (e as { response?: { data?: { code?: unknown } } } | null | undefined)?.response?.data?.code;
  return typeof code === 'string' && STALE_CODES.has(code);
};

/** Lỗi khi bấm xác nhận: dữ liệu đổi ⇒ câu KHÔNG mã + tải lại hệ quả; còn lại ⇒ chữ BE đã lọc (hoặc câu dự phòng). */
export const consequenceErrorView = (e: unknown, fallback: string): { stale: boolean; text: string } =>
  isStaleConsequenceError(e) ? { stale: true, text: CONS_STALE_TEXT } : { stale: false, text: friendlyApiError(e, fallback) };

/** 409 `IMPACT_CHANGED` của `POST /adaptation/scripts` (vân tay `expectedFingerprint` lệch ⇒ chưa mở job nào). */
export const isImpactChangedError = (e: unknown): boolean =>
  (e as { response?: { data?: { code?: unknown } } } | null | undefined)?.response?.data?.code === 'IMPACT_CHANGED';

/** Lỗi tổng hợp để `writeScripts` (nuốt lỗi, trả cờ) báo lại cho hộp hậu quả như một 409 thật. */
export const REWRITE_STALE = (): { response: { status: number; data: { code: string } } } => ({ response: { status: 409, data: { code: 'IMPACT_CHANGED' } } });
